import type { EscalationLevelInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import {
  createEscalationLevel,
  deactivateEscalationLevel,
  listEscalationLevels,
  reactivateEscalationLevel,
  updateEscalationLevel,
  type EscalationLevel,
} from '../../../reference-data.api';

export type EscalationLevelsState = { status: 'loading' } | { status: 'loaded'; levels: EscalationLevel[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): EscalationLevelsState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

function saveErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return 'There is already an escalation level at that number of days.';
  return 'Could not save — check the fields and try again.';
}

/** Same shape as useJobTypes: create/update keep their error inline, deactivate/reactivate toast. */
export function useEscalationLevels() {
  const [state, setState] = useState<EscalationLevelsState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listEscalationLevels()
      .then((levels) => {
        if (!cancelled) setState({ status: 'loaded', levels });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', levels: await listEscalationLevels() });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(saveErrorMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  const create = (input: EscalationLevelInput) => save(() => createEscalationLevel(input), `Escalation level ${input.label} created`);
  const update = (id: string, input: EscalationLevelInput) => save(() => updateEscalationLevel(id, input), `Escalation level ${input.label} updated`);
  const deactivate = (row: EscalationLevel) =>
    rowAction(() => deactivateEscalationLevel(row.id), `Escalation level ${row.label} deactivated`, `Couldn't deactivate ${row.label}`);
  const reactivate = (row: EscalationLevel) =>
    rowAction(() => reactivateEscalationLevel(row.id), `Escalation level ${row.label} reactivated`, `Couldn't reactivate ${row.label}`);

  return { state, saveError, saving, create, update, resetSaveError: () => setSaveError(null), deactivate, reactivate };
}
